import { computed, ref, Ref } from 'vue';
import { useRouter } from 'vue-router';
import { useToast } from 'primevue/usetoast';

import { useCurrentView } from '../composables/useCurrentView';
import { useCurrentSegment } from '../composables/useCurrentSegment';
import { useCurrentCalculation } from '../composables/useCurrentCalculation';
import { useNetworkStatus } from '../composables/useNetworkStatus';
import { useViewNavigation } from '../composables/useViewNavigation';
import { useSaveCalculation } from '../composables/useSaveCalculation';

import type { SegmentItemProps } from '../utils/types';

import { useCalculationsStore } from '../store/calculations';

export function useHandleNextView(selectedItems: Ref<{ [key: string]: number | null }>) {
  const router = useRouter();
  const toast = useToast();

  const { currentView } = useCurrentView();
  const { currentSegment } = useCurrentSegment();
  const { currentCalculation } = useCurrentCalculation();
  const { nextViewButton } = useViewNavigation();
  const { saveCalculation } = useSaveCalculation();

  const { isOnline } = useNetworkStatus();

  const calculationStore = useCalculationsStore();

  const isLoading = ref(false);

  const isValid = computed(() => {
    const unfilled = Object.keys(selectedItems.value).filter(
      (key) => selectedItems.value[key] === null
    );

    return unfilled.length === 0;
  });

  function checkCurrentValues() {
    if (!currentSegment.value || !currentCalculation.value || !currentView.value) {
      throw Error('brak currentSegment lub currentCalculation');
    }
  }

  function getUpdatedItems(): SegmentItemProps[] {
    return (
      currentView.value?.items.map((item) => {
        if (item.type === 'product') {
          return {
            item_id: item.id,
            product_price_id: Number(
              item.products.find(
                (product) => product.id === (selectedItems.value[item.id] as number)
              )?.product_price.id
            ),
            product_id: Number(selectedItems.value[item.id]),
            type: 'product',
          };
        }

        if (item.type === 'rule') {
          return {
            item_id: item.id,
            rule_id: item.rule_id,
            type: 'rule',
          };
        }

        return {
          item_id: item.id,
          type: 'value',
          value: selectedItems.value[item.id] as number | boolean | string,
        };
      }) || []
    );
  }

  async function handleNextView() {
    checkCurrentValues();

    const updatedItems = getUpdatedItems();

    const updatedCalculation = calculationStore.getUpdatedItemsCalculationSegment(
      String(currentCalculation.value?.uuid),
      Number(currentSegment.value?.id),
      updatedItems
    );

    calculationStore.saveCalculation({ ...updatedCalculation, outdated: true });

    router.push(nextViewButton.value.route);
  }

  async function handleNextGroup(route: {
    name: string;
    params: {
      [key: string]: any;
    };
  }) {
    checkCurrentValues();

    const updatedItems = getUpdatedItems();

    const updatedCalculation = calculationStore.getUpdatedItemsCalculationSegment(
      String(currentCalculation.value?.uuid),
      Number(currentSegment.value?.id),
      updatedItems
    );

    isLoading.value = true;

    await saveCalculation(updatedCalculation);

    isLoading.value = false;

    router.push(route);
  }

  return {
    isValid,
    isLoading,
    handleNextView,
    handleNextGroup,
  };
}
